import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Sparkles } from 'lucide-react';

/**
 * Static, English-only Privacy Policy page based on the Termly-generated notice
 * for AIgnyx (owned by Венета Димитрова Кирилова). This page is rendered from
 * clean semantic JSX (no Termly inline styles) and inherits AIgnyx typography
 * from tailwind (Fraunces headings via `font-heading`, Source Sans 3 body).
 */

const H2 = ({ id, children }) => (
  <h2
    id={id}
    className="font-heading text-2xl sm:text-3xl font-bold mt-14 mb-4 scroll-mt-24"
  >
    {children}
  </h2>
);

const H3 = ({ children }) => (
  <h3 className="font-heading text-lg font-semibold mt-8 mb-3">{children}</h3>
);

const P = ({ children }) => (
  <p className="text-sm sm:text-base leading-relaxed text-muted-foreground mb-4">
    {children}
  </p>
);

const UL = ({ children }) => (
  <ul className="list-disc pl-6 space-y-2 text-sm sm:text-base text-muted-foreground mb-4 leading-relaxed">
    {children}
  </ul>
);

const A = ({ href, children }) => {
  const isInternal = href.startsWith('#');
  if (isInternal) {
    return (
      <a href={href} className="text-primary hover:underline">
        {children}
      </a>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary hover:underline break-words"
    >
      {children}
    </a>
  );
};

const InShort = ({ children }) => (
  <p className="text-sm sm:text-base italic text-muted-foreground mb-4">
    <strong className="not-italic text-foreground">In Short:</strong> {children}
  </p>
);

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background" data-testid="privacy-page">
      <div className="noise-overlay" />

      {/* Sticky header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2" data-testid="privacy-back-home">
            <ArrowLeft className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Back home</span>
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading font-bold text-sm">AIgnyx</span>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <motion.article
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          <p className="text-xs text-muted-foreground mb-2">
            Last updated August 24, 2026
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-6">
            Privacy Policy
          </h1>

          {/* Intro */}
          <P>
            This Privacy Notice for <strong>Венета Димитрова Кирилова</strong> (&quot;
            <strong>we</strong>,&quot; &quot;<strong>us</strong>,&quot; or &quot;
            <strong>our</strong>&quot;), describes how and why we might access, collect,
            store, use, and/or share (&quot;<strong>process</strong>&quot;) your personal
            information when you use our services (&quot;<strong>Services</strong>&quot;),
            including when you:
          </P>
          <UL>
            <li>
              Visit our website at{' '}
              <A href="https://aignyx.com">https://aignyx.com</A> or any website of ours
              that links to this Privacy Notice
            </li>
            <li>
              Use AIgnyx. AIgnyx is an AI-powered platform that generates UGC-style
              advertising content — including hooks, scripts, storyboards, and
              video-ready materials — for businesses and marketers.
            </li>
            <li>
              Engage with us in other related ways, including any marketing or events
            </li>
          </UL>
          <P>
            <strong>Questions or concerns?</strong> Reading this Privacy Notice will help
            you understand your privacy rights and choices. We are responsible for making
            decisions about how your personal information is processed. If you do not
            agree with our policies and practices, please do not use our Services. If you
            still have any questions or concerns, please contact us at{' '}
            <A href="mailto:hello@aignyx.com">hello@aignyx.com</A>.
          </P>

          {/* Summary of key points */}
          <H2>Summary of Key Points</H2>
          <P>
            <em>
              This summary provides key points from our Privacy Notice, but you can find
              out more details about any of these topics by clicking the link following
              each key point or by using our <A href="#toc">table of contents</A> below
              to find the section you are looking for.
            </em>
          </P>
          <P>
            <strong>What personal information do we process?</strong> When you visit,
            use, or navigate our Services, we may process personal information depending
            on how you interact with us and the Services, the choices you make, and the
            products and features you use. Learn more about{' '}
            <A href="#personalinfo">personal information you disclose to us</A>.
          </P>
          <P>
            <strong>Do we process any sensitive personal information?</strong> Some of
            the information may be considered &quot;special&quot; or &quot;sensitive&quot;
            in certain jurisdictions, for example your racial or ethnic origins, sexual
            orientation, and religious beliefs. We do not process sensitive personal
            information.
          </P>
          <P>
            <strong>Do we collect any information from third parties?</strong> We do not
            collect any information from third parties.
          </P>
          <P>
            <strong>How do we process your information?</strong> We process your
            information to provide, improve, and administer our Services, communicate
            with you, for security and fraud prevention, and to comply with law. We may
            also process your information for other purposes with your consent. We
            process your information only when we have a valid legal reason to do so.
            Learn more about <A href="#infouse">how we process your information</A>.
          </P>
          <P>
            <strong>
              In what situations and with which types of parties do we share personal
              information?
            </strong>{' '}
            We may share information in specific situations and with specific categories
            of third parties. Learn more about{' '}
            <A href="#whoshare">
              when and with whom we share your personal information
            </A>
            .
          </P>
          <P>
            <strong>How do we keep your information safe?</strong> We have adequate
            organizational and technical processes and procedures in place to protect
            your personal information. However, no electronic transmission over the
            internet or information storage technology can be guaranteed to be 100%
            secure, so we cannot promise or guarantee that hackers, cybercriminals, or
            other unauthorized third parties will not be able to defeat our security and
            improperly collect, access, steal, or modify your information. Learn more
            about <A href="#infosafe">how we keep your information safe</A>.
          </P>
          <P>
            <strong>What are your rights?</strong> Depending on where you are located
            geographically, the applicable privacy law may mean you have certain rights
            regarding your personal information. Learn more about{' '}
            <A href="#privacyrights">your privacy rights</A>.
          </P>
          <P>
            <strong>How do you exercise your rights?</strong> The easiest way to exercise
            your rights is by visiting{' '}
            <A href="https://aignyx.com/dashboard/settings">
              https://aignyx.com/dashboard/settings
            </A>
            , or by contacting us. We will consider and act upon any request in
            accordance with applicable data protection laws.
          </P>
          <P>
            Want to learn more about what we do with any information we collect?{' '}
            <A href="#toc">Review the Privacy Notice in full</A>.
          </P>

          {/* Table of contents */}
          <H2 id="toc">Table of Contents</H2>
          <ol className="list-decimal pl-6 space-y-2 text-sm sm:text-base text-muted-foreground mb-4 leading-relaxed">
            <li><A href="#infocollect">What information do we collect?</A></li>
            <li><A href="#infouse">How do we process your information?</A></li>
            <li><A href="#legalbases">What legal bases do we rely on to process your personal information?</A></li>
            <li><A href="#whoshare">When and with whom do we share your personal information?</A></li>
            <li><A href="#cookies">Do we use cookies and other tracking technologies?</A></li>
            <li><A href="#ai">Do we offer artificial intelligence-based products?</A></li>
            <li><A href="#sociallogins">How do we handle your social logins?</A></li>
            <li><A href="#intltransfers">Is your information transferred internationally?</A></li>
            <li><A href="#inforetain">How long do we keep your information?</A></li>
            <li><A href="#infosafe">How do we keep your information safe?</A></li>
            <li><A href="#infominors">Do we collect information from minors?</A></li>
            <li><A href="#privacyrights">What are your privacy rights?</A></li>
            <li><A href="#DNT">Controls for do-not-track features</A></li>
            <li><A href="#uslaws">Do United States residents have specific privacy rights?</A></li>
            <li><A href="#policyupdates">Do we make updates to this notice?</A></li>
            <li><A href="#contact">How can you contact us about this notice?</A></li>
            <li><A href="#request">How can you review, update, or delete the data we collect from you?</A></li>
          </ol>

          {/* 1. What information do we collect */}
          <H2 id="infocollect">1. What Information Do We Collect?</H2>
          <H3 id="personalinfo">Personal information you disclose to us</H3>
          <InShort>
            <em>We collect personal information that you provide to us.</em>
          </InShort>
          <P>
            We collect personal information that you voluntarily provide to us when you
            register on the Services, express an interest in obtaining information about
            us or our products and Services, when you participate in activities on the
            Services, or otherwise when you contact us.
          </P>
          <P>
            <strong>Personal Information Provided by You.</strong> The personal
            information that we collect depends on the context of your interactions with
            us and the Services, the choices you make, and the products and features you
            use. The personal information we collect may include the following:
          </P>
          <UL>
            <li>names</li>
            <li>email addresses</li>
            <li>usernames</li>
            <li>passwords</li>
            <li>contact or authentication data</li>
            <li>billing addresses</li>
            <li>debit/credit card numbers</li>
          </UL>
          <P>
            <strong>Sensitive Information.</strong> We do not process sensitive
            information.
          </P>
          <P>
            <strong>Payment Data.</strong> We may collect data necessary to process your
            payment if you choose to make purchases, such as your payment instrument
            number, and the security code associated with your payment instrument. All
            payment data is handled and stored by Stripe. You may find their privacy
            notice link(s) here:{' '}
            <A href="https://stripe.com/privacy">https://stripe.com/privacy</A>.
          </P>
          <P>
            <strong>Social Media Login Data.</strong> We may provide you with the option
            to register with us using your existing social media account details, like
            your Facebook, X, or other social media account. If you choose to register in
            this way, we will collect certain profile information about you from the
            social media provider, as described in the section called{' '}
            <A href="#sociallogins">&quot;HOW DO WE HANDLE YOUR SOCIAL LOGINS?&quot;</A>{' '}
            below.
          </P>
          <P>
            All personal information that you provide to us must be true, complete, and
            accurate, and you must notify us of any changes to such personal information.
          </P>

          <H3>Information automatically collected</H3>
          <InShort>
            <em>
              Some information — such as your Internet Protocol (IP) address and/or
              browser and device characteristics — is collected automatically when you
              visit our Services.
            </em>
          </InShort>
          <P>
            We automatically collect certain information when you visit, use, or navigate
            the Services. This information does not reveal your specific identity (like
            your name or contact information) but may include device and usage
            information, such as your IP address, browser and device characteristics,
            operating system, language preferences, referring URLs, device name, country,
            location, information about how and when you use our Services, and other
            technical information. This information is primarily needed to maintain the
            security and operation of our Services, and for our internal analytics and
            reporting purposes.
          </P>
          <P>
            Like many businesses, we also collect information through cookies and similar
            technologies. You can find out more about this in our Cookie Notice:{' '}
            <A href="https://aignyx.com/cookies">https://aignyx.com/cookies</A>.
          </P>
          <P>The information we collect includes:</P>
          <UL>
            <li>
              <em>Log and Usage Data.</em> Log and usage data is service-related,
              diagnostic, usage, and performance information our servers automatically
              collect when you access or use our Services and which we record in log
              files. Depending on how you interact with us, this log data may include
              your IP address, device information, browser type, and settings and
              information about your activity in the Services (such as the date/time
              stamps associated with your usage, pages and files viewed, searches, and
              other actions you take such as which features you use), device event
              information (such as system activity, error reports (sometimes called
              &quot;crash dumps&quot;), and hardware settings).
            </li>
          </UL>
          <H3>Google API</H3>
          <P>
            Our use of information received from Google APIs will adhere to{' '}
            <A href="https://developers.google.com/terms/api-services-user-data-policy">
              Google API Services User Data Policy
            </A>
            , including the{' '}
            <A href="https://developers.google.com/terms/api-services-user-data-policy#limited-use">
              Limited Use requirements
            </A>
            .
          </P>

          {/* 2. How do we process */}
          <H2 id="infouse">2. How Do We Process Your Information?</H2>
          <InShort>
            <em>
              We process your information to provide, improve, and administer our
              Services, communicate with you, for security and fraud prevention, and to
              comply with law. We process the personal information for the following
              purposes listed below. We may also process your information for other
              purposes only with your prior explicit consent.
            </em>
          </InShort>
          <P>
            <strong>
              We process your personal information for a variety of reasons, depending on
              how you interact with our Services, including:
            </strong>
          </P>
          <UL>
            <li>
              <strong>
                To facilitate account creation and authentication and otherwise manage
                user accounts.
              </strong>{' '}
              We may process your information so you can create and log in to your
              account, as well as keep your account in working order.
            </li>
            <li>
              <strong>To deliver and facilitate delivery of services to the user.</strong>{' '}
              We may process your information to provide you with the requested service.
            </li>
            <li>
              <strong>To respond to user inquiries/offer support to users.</strong> We
              may process your information to respond to your inquiries and solve any
              potential issues you might have with the requested service.
            </li>
            <li>
              <strong>To send administrative information to you.</strong> We may process
              your information to send you details about our products and services,
              changes to our terms and policies, and other similar information.
            </li>
            <li>
              <strong>To fulfill and manage your orders.</strong> We may process your
              information to fulfill and manage your orders, payments, returns, and
              exchanges made through the Services.
            </li>
            <li>
              <strong>To save or protect an individual&apos;s vital interest.</strong> We
              may process your information when necessary to save or protect an
              individual&apos;s vital interest, such as to prevent harm.
            </li>
          </UL>

          {/* 3. Legal bases */}
          <H2 id="legalbases">
            3. What Legal Bases Do We Rely On to Process Your Information?
          </H2>
          <InShort>
            <em>
              We only process your personal information when we believe it is necessary
              and we have a valid legal reason (i.e., legal basis) to do so under
              applicable law, like with your consent, to comply with laws, to provide you
              with services to enter into or fulfill our contractual obligations, to
              protect your rights, or to fulfill our legitimate business interests.
            </em>
          </InShort>
          <P>
            <em>
              <strong>
                <u>If you are located in the EU or UK, this section applies to you.</u>
              </strong>
            </em>
          </P>
          <P>
            The General Data Protection Regulation (GDPR) and UK GDPR require us to
            explain the valid legal bases we rely on in order to process your personal
            information. As such, we may rely on the following legal bases to process
            your personal information:
          </P>
          <UL>
            <li>
              <strong>Consent.</strong> We may process your information if you have given
              us permission (i.e., consent) to use your personal information for a
              specific purpose. You can withdraw your consent at any time. Learn more
              about <A href="#withdrawconsent">withdrawing your consent</A>.
            </li>
            <li>
              <strong>Performance of a Contract.</strong> We may process your personal
              information when we believe it is necessary to fulfill our contractual
              obligations to you, including providing our Services or at your request
              prior to entering into a contract with you.
            </li>
            <li>
              <strong>Legal Obligations.</strong> We may process your information where
              we believe it is necessary for compliance with our legal obligations, such
              as to cooperate with a law enforcement body or regulatory agency, exercise
              or defend our legal rights, or disclose your information as evidence in
              litigation in which we are involved.
            </li>
            <li>
              <strong>Vital Interests.</strong> We may process your information where we
              believe it is necessary to protect your vital interests or the vital
              interests of a third party, such as situations involving potential threats
              to the safety of any person.
            </li>
          </UL>
          <P>
            <em>
              <strong>
                <u>If you are located in Canada, this section applies to you.</u>
              </strong>
            </em>
          </P>
          <P>
            We may process your information if you have given us specific permission
            (i.e., express consent) to use your personal information for a specific
            purpose, or in situations where your permission can be inferred (i.e.,
            implied consent). You can{' '}
            <A href="#withdrawconsent">withdraw your consent</A> at any time.
          </P>
          <P>
            In some exceptional cases, we may be legally permitted under applicable law
            to process your information without your consent, including, for example:
          </P>
          <UL>
            <li>
              If collection is clearly in the interests of an individual and consent
              cannot be obtained in a timely way
            </li>
            <li>For investigations and fraud detection and prevention</li>
            <li>For business transactions provided certain conditions are met</li>
            <li>
              If it is contained in a witness statement and the collection is necessary
              to assess, process, or settle an insurance claim
            </li>
            <li>
              For identifying injured, ill, or deceased persons and communicating with
              next of kin
            </li>
            <li>
              If we have reasonable grounds to believe an individual has been, is, or may
              be victim of financial abuse
            </li>
            <li>
              If it is reasonable to expect collection and use with consent would
              compromise the availability or the accuracy of the information and the
              collection is reasonable for purposes related to investigating a breach of
              an agreement or a contravention of the laws of Canada or a province
            </li>
            <li>
              If disclosure is required to comply with a subpoena, warrant, court order,
              or rules of the court relating to the production of records
            </li>
            <li>
              If it was produced by an individual in the course of their employment,
              business, or profession and the collection is consistent with the purposes
              for which the information was produced
            </li>
            <li>
              If the collection is solely for journalistic, artistic, or literary
              purposes
            </li>
            <li>
              If the information is publicly available and is specified by the
              regulations
            </li>
            <li>
              We may disclose de-identified information for approved research or
              statistics projects, subject to ethics oversight and confidentiality
              commitments
            </li>
          </UL>

          {/* 4. Sharing */}
          <H2 id="whoshare">
            4. When and With Whom Do We Share Your Personal Information?
          </H2>
          <InShort>
            <em>
              We may share information in specific situations described in this section
              and/or with the following categories of third parties.
            </em>
          </InShort>
          <P>
            <strong>Vendors, Consultants, and Other Third-Party Service Providers.</strong>{' '}
            We may share your data with third-party vendors, service providers,
            contractors, or agents (&quot;<strong>third parties</strong>&quot;) who
            perform services for us or on our behalf and require access to such
            information to do that work. We have contracts in place with our third
            parties, which are designed to help safeguard your personal information. This
            means that they cannot do anything with your personal information unless we
            have instructed them to do it. They will also not share your personal
            information with any organization apart from us. They also commit to protect
            the data they hold on our behalf and to retain it for the period we instruct.
          </P>
          <P>
            The categories of third parties we may share personal information with are as
            follows:
          </P>
          <UL>
            <li>AI Platforms</li>
            <li>Cloud Computing Services</li>
            <li>Data Storage Service Providers</li>
            <li>Payment Processors</li>
            <li>User Account Registration &amp; Authentication Services</li>
            <li>Website Hosting Service Providers</li>
          </UL>
          <P>
            We also may need to share your personal information in the following
            situations:
          </P>
          <UL>
            <li>
              <strong>Business Transfers.</strong> We may share or transfer your
              information in connection with, or during negotiations of, any merger, sale
              of company assets, financing, or acquisition of all or a portion of our
              business to another company.
            </li>
          </UL>

          {/* 5. Cookies */}
          <H2 id="cookies">5. Do We Use Cookies and Other Tracking Technologies?</H2>
          <InShort>
            <em>
              We may use cookies and other tracking technologies to collect and store
              your information.
            </em>
          </InShort>
          <P>
            We may use cookies and similar tracking technologies (like web beacons and
            pixels) to gather information when you interact with our Services. Some
            online tracking technologies help us maintain the security of our Services
            and your account, prevent crashes, fix bugs, save your preferences, and
            assist with basic site functions.
          </P>
          <P>
            We also permit third parties and service providers to use online tracking
            technologies on our Services for analytics and advertising, including to help
            manage and display advertisements or to tailor advertisements to your
            interests. The third parties and service providers use their technology to
            provide advertising about products and services tailored to your interests
            which may appear either on our Services or on other websites.
          </P>
          <P>
            To the extent these online tracking technologies are deemed to be a
            &quot;sale&quot;/&quot;sharing&quot; (which includes targeted advertising, as
            defined under the applicable laws) under applicable US state laws, you can
            opt out of these online tracking technologies by submitting a request as
            described below under section{' '}
            <A href="#uslaws">
              &quot;DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?&quot;
            </A>
          </P>
          <P>
            Specific information about how we use such technologies and how you can
            refuse certain cookies is set out in our Cookie Notice:{' '}
            <A href="https://aignyx.com/cookies">https://aignyx.com/cookies</A>.
          </P>

          {/* 6. AI */}
          <H2 id="ai">6. Do We Offer Artificial Intelligence-Based Products?</H2>
          <InShort>
            <em>
              We offer products, features, or tools powered by artificial intelligence,
              machine learning, or similar technologies.
            </em>
          </InShort>
          <P>
            As part of our Services, we offer products, features, or tools powered by
            artificial intelligence, machine learning, or similar technologies
            (collectively, &quot;AI Products&quot;). These tools are designed to enhance
            your experience and provide you with innovative solutions. The terms in this
            Privacy Notice govern your use of the AI Products within our Services.
          </P>
          <H3>Use of AI Technologies</H3>
          <P>
            We provide the AI Products through third-party service providers (&quot;AI
            Service Providers&quot;), including Anthropic. As outlined in this Privacy
            Notice, your input, output, and personal information will be shared with and
            processed by these AI Service Providers to enable your use of our AI Products
            for purposes outlined in{' '}
            <A href="#legalbases">
              &quot;WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR PERSONAL
              INFORMATION?&quot;
            </A>
            . You must not use the AI Products in any way that violates the terms or
            policies of any AI Service Provider.
          </P>
          <H3>Our AI Products</H3>
          <P>Our AI Products are designed for the following functions:</P>
          <UL>
            <li>AI document generation</li>
          </UL>
          <H3>How We Process Your Data Using AI</H3>
          <P>
            All personal information processed using our AI Products is handled in line
            with our Privacy Notice and our agreement with third parties. This ensures
            high security and safeguards your personal information throughout the
            process, giving you peace of mind about your data&apos;s safety.
          </P>

          {/* 7. Social logins */}
          <H2 id="sociallogins">7. How Do We Handle Your Social Logins?</H2>
          <InShort>
            <em>
              If you choose to register or log in to our Services using a social media
              account, we may have access to certain information about you.
            </em>
          </InShort>
          <P>
            Our Services offer you the ability to register and log in using your
            third-party social media account details (like your Facebook or X logins).
            Where you choose to do this, we will receive certain profile information
            about you from your social media provider. The profile information we receive
            may vary depending on the social media provider concerned, but will often
            include your name, email address, friends list, and profile picture, as well
            as other information you choose to make public on such a social media
            platform.
          </P>
          <P>
            We will use the information we receive only for the purposes that are
            described in this Privacy Notice or that are otherwise made clear to you on
            the relevant Services. Please note that we do not control, and are not
            responsible for, other uses of your personal information by your third-party
            social media provider. We recommend that you review their privacy notice to
            understand how they collect, use, and share your personal information, and
            how you can set your privacy preferences on their sites and apps.
          </P>

          {/* 8. International transfers */}
          <H2 id="intltransfers">8. Is Your Information Transferred Internationally?</H2>
          <InShort>
            <em>
              We may transfer, store, and process your information in countries other
              than your own.
            </em>
          </InShort>
          <P>
            Our servers are located in the United States. Regardless of your location,
            please be aware that your information may be transferred to, stored by, and
            processed by us in our facilities and in the facilities of the third parties
            with whom we may share your personal information (see{' '}
            <A href="#whoshare">
              &quot;WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?&quot;
            </A>{' '}
            above), including facilities in the United States and other countries.
          </P>
          <P>
            If you are a resident in the European Economic Area (EEA), United Kingdom
            (UK), or Switzerland, then these countries may not necessarily have data
            protection laws or other similar laws as comprehensive as those in your
            country. However, we will take all necessary measures to protect your
            personal information in accordance with this Privacy Notice and applicable
            law.
          </P>
          <P>European Commission&apos;s Standard Contractual Clauses:</P>
          <P>
            We have implemented measures to protect your personal information, including
            by using the European Commission&apos;s Standard Contractual Clauses for
            transfers of personal information between our group companies and between us
            and our third-party providers. These clauses require all recipients to
            protect all personal information that they process originating from the EEA
            or UK in accordance with European data protection laws and regulations. Our
            Standard Contractual Clauses can be provided upon request. We have
            implemented similar appropriate safeguards with our third-party service
            providers and partners and further details can be provided upon request.
          </P>

          {/* 9. Retention */}
          <H2 id="inforetain">9. How Long Do We Keep Your Information?</H2>
          <InShort>
            <em>
              We keep your information for as long as necessary to fulfill the purposes
              outlined in this Privacy Notice unless otherwise required by law.
            </em>
          </InShort>
          <P>
            We will only keep your personal information for as long as it is necessary
            for the purposes set out in this Privacy Notice, unless a longer retention
            period is required or permitted by law (such as tax, accounting, or other
            legal requirements). No purpose in this notice will require us keeping your
            personal information for longer than the period of time in which users have
            an account with us.
          </P>
          <P>
            When we have no ongoing legitimate business need to process your personal
            information, we will either delete or anonymize such information, or, if this
            is not possible (for example, because your personal information has been
            stored in backup archives), then we will securely store your personal
            information and isolate it from any further processing until deletion is
            possible.
          </P>

          {/* 10. Safety */}
          <H2 id="infosafe">10. How Do We Keep Your Information Safe?</H2>
          <InShort>
            <em>
              We aim to protect your personal information through a system of
              organizational and technical security measures.
            </em>
          </InShort>
          <P>
            We have implemented appropriate and reasonable technical and organizational
            security measures designed to protect the security of any personal
            information we process. However, despite our safeguards and efforts to secure
            your information, no electronic transmission over the Internet or information
            storage technology can be guaranteed to be 100% secure, so we cannot promise
            or guarantee that hackers, cybercriminals, or other unauthorized third
            parties will not be able to defeat our security and improperly collect,
            access, steal, or modify your information. Although we will do our best to
            protect your personal information, transmission of personal information to
            and from our Services is at your own risk. You should only access the
            Services within a secure environment.
          </P>

          {/* 11. Minors */}
          <H2 id="infominors">11. Do We Collect Information From Minors?</H2>
          <InShort>
            <em>
              We do not knowingly collect data from or market to children under 18 years
              of age or the equivalent age as specified by law in your jurisdiction.
            </em>
          </InShort>
          <P>
            We do not knowingly collect, solicit data from, or market to children under
            18 years of age or the equivalent age as specified by law in your
            jurisdiction, nor do we knowingly sell such personal information. By using
            the Services, you represent that you are at least 18 or the equivalent age as
            specified by law in your jurisdiction or that you are the parent or guardian
            of such a minor and consent to such minor dependent&apos;s use of the
            Services. If we learn that personal information from users less than 18 years
            of age or the equivalent age as specified by law in your jurisdiction has
            been collected, we will deactivate the account and take reasonable measures
            to promptly delete such data from our records. If you become aware of any
            data we may have collected from children under age 18 or the equivalent age
            as specified by law in your jurisdiction, please contact us at{' '}
            <A href="mailto:hello@aignyx.com">hello@aignyx.com</A>.
          </P>

          {/* 12. Privacy rights */}
          <H2 id="privacyrights">12. What Are Your Privacy Rights?</H2>
          <InShort>
            <em>
              Depending on your state of residence in the US or in some regions, such as
              the European Economic Area (EEA), United Kingdom (UK), Switzerland, and
              Canada, you have rights that allow you greater access to and control over
              your personal information. You may review, change, or terminate your
              account at any time, depending on your country, province, or state of
              residence.
            </em>
          </InShort>
          <P>
            In some regions (like the EEA, UK, Switzerland, and Canada), you have certain
            rights under applicable data protection laws. These may include the right (i)
            to request access and obtain a copy of your personal information, (ii) to
            request rectification or erasure; (iii) to restrict the processing of your
            personal information; (iv) if applicable, to data portability; and (v) not to
            be subject to automated decision-making. If a decision that produces legal or
            similarly significant effects is made solely by automated means, we will
            inform you, explain the main factors, and offer a simple way to request human
            review. In certain circumstances, you may also have the right to object to
            the processing of your personal information. You can make such a request by
            contacting us by using the contact details provided in the section{' '}
            <A href="#contact">
              &quot;HOW CAN YOU CONTACT US ABOUT THIS NOTICE?&quot;
            </A>{' '}
            below.
          </P>
          <P>
            We will consider and act upon any request in accordance with applicable data
            protection laws.
          </P>
          <P>
            If you are located in the UK and are unhappy with how we have handled your
            personal information, you can make a complaint directly to us. This is in
            addition to the rights you have under the UK General Data Protection
            Regulation and the Data Protection Act 2018.
          </P>
          <P>How to contact us:</P>
          <UL>
            <li>
              <strong>Email:</strong>{' '}
              <A href="mailto:hello@aignyx.com">hello@aignyx.com</A>
            </li>
            <li>
              <strong>Post:</strong> See{' '}
              <A href="#contact">
                &quot;HOW CAN YOU CONTACT US ABOUT THIS NOTICE?&quot;
              </A>
            </li>
          </UL>
          <P>What happens after you complain:</P>
          <UL>
            <li>We will acknowledge your complaint within 30 days of receiving it.</li>
            <li>We will investigate without unjustifiable or excessive delay.</li>
            <li>We will keep you informed of progress and explain the outcome.</li>
          </UL>
          <P>
            If you are not happy with our final response, you can refer your complaint to
            the Information Commissioner&apos;s Office, the UK supervisory authority.
          </P>
          <UL>
            <li>
              <strong>Website:</strong>{' '}
              <A href="http://ico.org.uk/make-a-complaint">
                ico.org.uk/make-a-complaint
              </A>
            </li>
            <li>
              <strong>Helpline:</strong> 0303 123 1113
            </li>
            <li>
              <strong>Post:</strong> Information Commissioner&apos;s Office, Wycliffe
              House, Water Lane, Wilmslow, Cheshire, SK9 5AF
            </li>
          </UL>
          <P>
            If you are located in the EEA or UK and you believe we are unlawfully
            processing your personal information, you also have the right to complain to
            your{' '}
            <A href="https://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm">
              Member State data protection authority
            </A>{' '}
            or{' '}
            <A href="https://ico.org.uk/make-a-complaint/data-protection-complaints/data-protection-complaints/">
              UK data protection authority
            </A>
            .
          </P>
          <P>
            If you are located in Switzerland, you may contact the{' '}
            <A href="https://www.edoeb.admin.ch/edoeb/en/home.html">
              Federal Data Protection and Information Commissioner
            </A>
            .
          </P>
          <H3 id="withdrawconsent">Withdrawing your consent:</H3>
          <P>
            If we are relying on your consent to process your personal information, which
            may be express and/or implied consent depending on the applicable law, you
            have the right to withdraw your consent at any time. You can withdraw your
            consent at any time by contacting us by using the contact details provided in
            the section{' '}
            <A href="#contact">
              &quot;HOW CAN YOU CONTACT US ABOUT THIS NOTICE?&quot;
            </A>{' '}
            below.
          </P>
          <P>
            However, please note that this will not affect the lawfulness of the
            processing before its withdrawal nor, when applicable law allows, will it
            affect the processing of your personal information conducted in reliance on
            lawful processing grounds other than consent.
          </P>
          <H3>Opting out of marketing and promotional communications:</H3>
          <P>
            You can unsubscribe from our marketing and promotional communications at any
            time by clicking on the unsubscribe link in the emails that we send, or by
            contacting us using the details provided in the section{' '}
            <A href="#contact">
              &quot;HOW CAN YOU CONTACT US ABOUT THIS NOTICE?&quot;
            </A>{' '}
            below. You will then be removed from the marketing lists. However, we may
            still communicate with you — for example, to send you service-related
            messages that are necessary for the administration and use of your account,
            to respond to service requests, or for other non-marketing purposes.
          </P>
          <H3>Account Information</H3>
          <P>
            If you would at any time like to review or change the information in your
            account or terminate your account, you can:
          </P>
          <UL>
            <li>Contact us using the contact information provided.</li>
          </UL>
          <P>
            Upon your request to terminate your account, we will deactivate or delete
            your account and information from our active databases. However, we may
            retain some information in our files to prevent fraud, troubleshoot problems,
            assist with any investigations, enforce our legal terms and/or comply with
            applicable legal requirements.
          </P>
          <P>
            <strong>
              <u>Cookies and similar technologies:</u>
            </strong>{' '}
            Most Web browsers are set to accept cookies by default. If you prefer, you
            can usually choose to set your browser to remove cookies and to reject
            cookies. If you choose to remove cookies or reject cookies, this could affect
            certain features or services of our Services. For further information, please
            see our Cookie Notice:{' '}
            <A href="https://aignyx.com/cookies">https://aignyx.com/cookies</A>.
          </P>
          <P>
            If you have questions or comments about your privacy rights, you may email us
            at <A href="mailto:hello@aignyx.com">hello@aignyx.com</A>.
          </P>

          {/* 13. DNT */}
          <H2 id="DNT">13. Controls For Do-Not-Track Features</H2>
          <P>
            Most web browsers and some mobile operating systems and mobile applications
            include a Do-Not-Track (&quot;DNT&quot;) feature or setting you can activate
            to signal your privacy preference not to have data about your online browsing
            activities monitored and collected. At this stage, no uniform technology
            standard for recognizing and implementing DNT signals has been finalized. As
            such, we do not currently respond to DNT browser signals or any other
            mechanism that automatically communicates your choice not to be tracked
            online. If a standard for online tracking is adopted that we must follow in
            the future, we will inform you about that practice in a revised version of
            this Privacy Notice.
          </P>
          <P>
            California law requires us to let you know how we respond to web browser DNT
            signals. Because there currently is not an industry or legal standard for
            recognizing or honoring DNT signals, we do not respond to them at this time.
          </P>

          {/* 14. US Laws */}
          <H2 id="uslaws">
            14. Do United States Residents Have Specific Privacy Rights?
          </H2>
          <InShort>
            <em>
              If you are a resident of California, Colorado, Connecticut, Delaware,
              Florida, Indiana, Iowa, Kentucky, Maryland, Minnesota, Montana, Nebraska,
              New Hampshire, New Jersey, Oregon, Rhode Island, Tennessee, Texas, Utah, or
              Virginia, you may have the right to request access to and receive details
              about the personal information we maintain about you and how we have
              processed it, correct inaccuracies, get a copy of, or delete your personal
              information. You may also have the right to withdraw your consent to our
              processing of your personal information. These rights may be limited in
              some circumstances by applicable law. More information is provided below.
            </em>
          </InShort>
          <H3>Categories of Personal Information We Collect</H3>
          <P>
            The table below shows the categories of personal information we have
            collected in the past twelve (12) months. The table includes illustrative
            examples of each category and does not reflect the personal information we
            collect from you. For a comprehensive inventory of all personal information
            we process, please refer to the section{' '}
            <A href="#infocollect">&quot;WHAT INFORMATION DO WE COLLECT?&quot;</A>
          </P>
          <div className="overflow-x-auto my-6">
            <table className="w-full text-xs sm:text-sm border-collapse border border-border">
              <thead>
                <tr className="bg-muted/40">
                  <th className="border border-border p-3 text-left font-semibold">
                    Category
                  </th>
                  <th className="border border-border p-3 text-left font-semibold">
                    Examples
                  </th>
                  <th className="border border-border p-3 text-center font-semibold w-24">
                    Collected
                  </th>
                </tr>
              </thead>
              <tbody className="text-muted-foreground">
                <tr>
                  <td className="border border-border p-3">A. Identifiers</td>
                  <td className="border border-border p-3">
                    Contact details, such as real name, alias, postal address, telephone
                    or mobile contact number, unique personal identifier, online
                    identifier, Internet Protocol address, email address, and account
                    name
                  </td>
                  <td className="border border-border p-3 text-center">YES</td>
                </tr>
                <tr>
                  <td className="border border-border p-3">
                    B. Personal information as defined in the California Customer Records
                    statute
                  </td>
                  <td className="border border-border p-3">
                    Name, contact information, education, employment, employment history,
                    and financial information
                  </td>
                  <td className="border border-border p-3 text-center">YES</td>
                </tr>
                <tr>
                  <td className="border border-border p-3">
                    C. Protected classification characteristics under state or federal
                    law
                  </td>
                  <td className="border border-border p-3">
                    Gender, age, date of birth, race and ethnicity, national origin,
                    marital status, and other demographic data
                  </td>
                  <td className="border border-border p-3 text-center">NO</td>
                </tr>
                <tr>
                  <td className="border border-border p-3">D. Commercial information</td>
                  <td className="border border-border p-3">
                    Transaction information, purchase history, financial details, and
                    payment information
                  </td>
                  <td className="border border-border p-3 text-center">YES</td>
                </tr>
                <tr>
                  <td className="border border-border p-3">E. Biometric information</td>
                  <td className="border border-border p-3">
                    Fingerprints and voiceprints
                  </td>
                  <td className="border border-border p-3 text-center">NO</td>
                </tr>
                <tr>
                  <td className="border border-border p-3">
                    F. Internet or other similar network activity
                  </td>
                  <td className="border border-border p-3">
                    Browsing history, search history, online behavior, interest data, and
                    interactions with our and other websites, applications, systems, and
                    advertisements
                  </td>
                  <td className="border border-border p-3 text-center">YES</td>
                </tr>
                <tr>
                  <td className="border border-border p-3">G. Geolocation data</td>
                  <td className="border border-border p-3">Device location</td>
                  <td className="border border-border p-3 text-center">NO</td>
                </tr>
                <tr>
                  <td className="border border-border p-3">
                    H. Audio, electronic, sensory, or similar information
                  </td>
                  <td className="border border-border p-3">
                    Images and audio, video or call recordings created in connection with
                    our business activities
                  </td>
                  <td className="border border-border p-3 text-center">YES</td>
                </tr>
                <tr>
                  <td className="border border-border p-3">
                    I. Professional or employment-related information
                  </td>
                  <td className="border border-border p-3">
                    Business contact details in order to provide you our Services at a
                    business level or job title, work history, and professional
                    qualifications if you apply for a job with us
                  </td>
                  <td className="border border-border p-3 text-center">NO</td>
                </tr>
                <tr>
                  <td className="border border-border p-3">J. Education Information</td>
                  <td className="border border-border p-3">
                    Student records and directory information
                  </td>
                  <td className="border border-border p-3 text-center">NO</td>
                </tr>
                <tr>
                  <td className="border border-border p-3">
                    K. Inferences drawn from collected personal information
                  </td>
                  <td className="border border-border p-3">
                    Inferences drawn from any of the collected personal information
                    listed above to create a profile or summary about, for example, an
                    individual&apos;s preferences and characteristics
                  </td>
                  <td className="border border-border p-3 text-center">NO</td>
                </tr>
                <tr>
                  <td className="border border-border p-3">
                    L. Sensitive personal Information
                  </td>
                  <td className="border border-border p-3">—</td>
                  <td className="border border-border p-3 text-center">NO</td>
                </tr>
              </tbody>
            </table>
          </div>
          <P>
            We may also collect other personal information outside of these categories
            through instances where you interact with us in person, online, or by phone
            or mail in the context of:
          </P>
          <UL>
            <li>Receiving help through our customer support channels;</li>
            <li>Participation in customer surveys or contests; and</li>
            <li>
              Facilitation in the delivery of our Services and to respond to your
              inquiries.
            </li>
          </UL>
          <P>
            We will use and retain the collected personal information as needed to
            provide the Services or for:
          </P>
          <UL>
            <li>Category A - As long as the user has an account with us</li>
            <li>Category B - As long as the user has an account with us</li>
            <li>Category D - As long as the user has an account with us</li>
            <li>Category F - As long as the user has an account with us</li>
            <li>Category H - As long as the user has an account with us</li>
          </UL>
          <H3>Sources of Personal Information</H3>
          <P>
            Learn more about the sources of personal information we collect in{' '}
            <A href="#infocollect">&quot;WHAT INFORMATION DO WE COLLECT?&quot;</A>
          </P>
          <H3>How We Use and Share Personal Information</H3>
          <P>
            Learn more about how we use your personal information in the section,{' '}
            <A href="#infouse">&quot;HOW DO WE PROCESS YOUR INFORMATION?&quot;</A>
          </P>
          <P>
            <strong>Will your information be shared with anyone else?</strong>
          </P>
          <P>
            We may disclose your personal information with our service providers pursuant
            to a written contract between us and each service provider. Learn more about
            how we disclose personal information to in the section,{' '}
            <A href="#whoshare">
              &quot;WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?&quot;
            </A>
          </P>
          <P>
            We may use your personal information for our own business purposes, such as
            for undertaking internal research for technological development and
            demonstration. This is not considered to be &quot;selling&quot; of your
            personal information.
          </P>
          <P>
            We have not sold or shared any personal information to third parties for a
            business or commercial purpose in the preceding twelve (12) months. We have
            disclosed the following categories of personal information to third parties
            for a business or commercial purpose in the preceding twelve (12) months:
          </P>
          <P>
            The categories of third parties to whom we disclosed personal information for
            a business or commercial purpose can be found under{' '}
            <A href="#whoshare">
              &quot;WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?&quot;
            </A>
          </P>

          <H3>Your Rights</H3>
          <P>
            You have rights under certain US state data protection laws. However, these
            rights are not absolute, and in certain cases, we may decline your request as
            permitted by law. These rights include:
          </P>
          <UL>
            <li>
              <strong>Right to know</strong> whether or not we are processing your
              personal data
            </li>
            <li>
              <strong>Right to access</strong> your personal data
            </li>
            <li>
              <strong>Right to correct</strong> inaccuracies in your personal data
            </li>
            <li>
              <strong>Right to request</strong> the deletion of your personal data
            </li>
            <li>
              <strong>Right to obtain a copy</strong> of the personal data you previously
              shared with us
            </li>
            <li>
              <strong>Right to non-discrimination</strong> for exercising your rights
            </li>
            <li>
              <strong>Right to opt out</strong> of the processing of your personal data
              if it is used for targeted advertising (or sharing as defined under
              California&apos;s privacy law), the sale of personal data, or profiling in
              furtherance of decisions that produce legal or similarly significant
              effects (&quot;profiling&quot;)
            </li>
          </UL>
          <P>
            Depending upon the state where you live, you may also have the following
            rights:
          </P>
          <UL>
            <li>
              Right to access the categories of personal data being processed (as
              permitted by applicable law, including the privacy law in Minnesota)
            </li>
            <li>
              Right to obtain a list of the categories of third parties to which we have
              disclosed personal data (as permitted by applicable law, including the
              privacy law in California, Delaware, and Maryland)
            </li>
            <li>
              Right to obtain a list of specific third parties to which we have disclosed
              personal data (as permitted by applicable law, including the privacy law in
              Minnesota and Oregon)
            </li>
            <li>
              Right to obtain a list of third parties to which we have sold personal data
              (as permitted by applicable law, including the privacy law in Connecticut)
            </li>
            <li>
              Right to review, understand, question, and depending on where you live,
              correct how personal data has been profiled (as permitted by applicable
              law, including the privacy law in Connecticut and Minnesota)
            </li>
            <li>
              Right to limit use and disclosure of sensitive personal data (as permitted
              by applicable law, including the privacy law in California)
            </li>
            <li>
              Right to opt out of the collection of sensitive data and personal data
              collected through the operation of a voice or facial recognition feature
              (as permitted by applicable law, including the privacy law in Florida)
            </li>
          </UL>
          <H3>How to Exercise Your Rights</H3>
          <P>
            To exercise these rights, you can contact us by visiting{' '}
            <A href="https://aignyx.com/dashboard/settings">
              https://aignyx.com/dashboard/settings
            </A>
            , by emailing us at{' '}
            <A href="mailto:hello@aignyx.com">hello@aignyx.com</A>, or by referring to
            the contact details at the bottom of this document.
          </P>
          <P>
            Under certain US state data protection laws, you can designate an authorized
            agent to make a request on your behalf. We may deny a request from an
            authorized agent that does not submit proof that they have been validly
            authorized to act on your behalf in accordance with applicable laws.
          </P>
          <H3>Request Verification</H3>
          <P>
            Upon receiving your request, we will need to verify your identity to
            determine you are the same person about whom we have the information in our
            system. We will only use personal information provided in your request to
            verify your identity or authority to make the request. However, if we cannot
            verify your identity from the information already maintained by us, we may
            request that you provide additional information for the purposes of verifying
            your identity and for security or fraud-prevention purposes.
          </P>
          <P>
            If you submit the request through an authorized agent, we may need to collect
            additional information to verify your identity before processing your request
            and the agent will need to provide a written and signed permission from you
            to submit such request on your behalf.
          </P>
          <H3>Appeals</H3>
          <P>
            Under certain US state data protection laws, if we decline to take action
            regarding your request, you may appeal our decision by emailing us at{' '}
            <A href="mailto:hello@aignyx.com">hello@aignyx.com</A>. We will inform you in
            writing of any action taken or not taken in response to the appeal, including
            a written explanation of the reasons for the decisions. If your appeal is
            denied, you may submit a complaint to your state attorney general.
          </P>
          <H3>California &quot;Shine The Light&quot; Law</H3>
          <P>
            California Civil Code Section 1798.83, also known as the &quot;Shine The
            Light&quot; law, permits our users who are California residents to request
            and obtain from us, once a year and free of charge, information about
            categories of personal information (if any) we disclosed to third parties for
            direct marketing purposes and the names and addresses of all third parties
            with which we shared personal information in the immediately preceding
            calendar year. If you are a California resident and would like to make such a
            request, please submit your request in writing to us by using the contact
            details provided in the section{' '}
            <A href="#contact">
              &quot;HOW CAN YOU CONTACT US ABOUT THIS NOTICE?&quot;
            </A>
          </P>

          {/* 15. Updates */}
          <H2 id="policyupdates">15. Do We Make Updates To This Notice?</H2>
          <InShort>
            <em>Yes, we will update this notice as necessary to stay compliant with relevant laws.</em>
          </InShort>
          <P>
            We may update this Privacy Notice from time to time. The updated version will
            be indicated by an updated &quot;Revised&quot; date at the top of this
            Privacy Notice. If we make material changes to this Privacy Notice, we may
            notify you either by prominently posting a notice of such changes or by
            directly sending you a notification. We encourage you to review this Privacy
            Notice frequently to be informed of how we are protecting your information.
          </P>

          {/* 16. Contact */}
          <H2 id="contact">16. How Can You Contact Us About This Notice?</H2>
          <P>
            If you have questions or comments about this notice, you may email us at{' '}
            <A href="mailto:hello@aignyx.com">hello@aignyx.com</A> or contact us by post
            at:
          </P>
          <div className="text-sm sm:text-base text-muted-foreground mb-4 leading-relaxed">
            <p>Veneta Kirilova</p>
            <p>Kozloduy 3320</p>
            <p>Bulgaria</p>
          </div>

          {/* 17. Review, update or delete */}
          <H2 id="request">
            17. How Can You Review, Update, Or Delete The Data We Collect From You?
          </H2>
          <P>
            Based on the applicable laws of your country or state of residence in the US,
            you may have the right to request access to the personal information we
            collect from you, details about how we have processed it, correct
            inaccuracies, or delete your personal information. You may also have the
            right to withdraw your consent to our processing of your personal
            information. These rights may be limited in some circumstances by applicable
            law. To request to review, update, or delete your personal information,
            please visit:{' '}
            <A href="https://aignyx.com/dashboard/settings">
              https://aignyx.com/dashboard/settings
            </A>
            .
          </P>

          {/* Footer */}
          <div className="mt-16 pt-6 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
            <span>© 2026 AIgnyx</span>
            <div className="flex gap-4">
              <Link to="/privacy" className="hover:text-primary">
                Privacy
              </Link>
              <Link to="/terms" className="hover:text-primary">
                Terms
              </Link>
            </div>
          </div>
        </motion.article>
      </main>
    </div>
  );
}
